/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback } from 'react'
import { notification } from 'antd'
import { useFormContext } from 'react-hook-form'
import * as Icons from '@ant-design/icons'
import { useParams } from 'react-router'
import _ from 'lodash'

import { Guid } from 'admin/lib/guid'
import { Matchers } from 'admin/lib/Records'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'

export const CopyPasteItem = props => {
  const form = useFormContext()
  const { accountId, moduleName } = useParams()

  const getLineItems = () => {
    // Get item values from state
    const tempItems = { ...useLineItemStore.getState() }

    // remove the 'deleteKey' and 'duplicateKey' keys from this object
    delete tempItems['deleteKey']
    delete tempItems['duplicateKey']

    // Convert nested objects to array of line item objects and remove hookFormId properties
    const items = Object.values(tempItems)
      .filter(item => typeof item === 'object')
      .map(item => {
        delete item['hookFormId']
        delete item['raw_hookFormId']
        return item
      })

    return items
  }

  const handleCopy = useCallback(
    async (filter = null) => {
      const formValues = form.getValues()
      const recordData = formValues.data ? formValues.data : null
      const recordEnriched = formValues.enriched ? formValues.enriched : null
      const recordRaw = formValues.raw ? formValues.raw : {}

      const items = getLineItems()

      const dataItems = []
      const enrichedItems = []
      const rawItems = []

      if (filter !== 'top-section-only') {
        for (const item of items ?? []) {
          const newDataItem = {}
          // We still need to create the Enriched item, but it should be empty
          const newEnrichedItem = {}
          const newRawItem = {}

          Object.keys(item).forEach(key => {
            if (key.startsWith('raw_')) {
              newRawItem[key.substring(4)] = item[key]
            } else {
              newDataItem[key] = item[key]
            }
          })

          // Remove id's
          delete newDataItem['id']
          delete newDataItem['hookFormId']
          delete newEnrichedItem['id']
          delete newEnrichedItem['hookFormId']
          delete newRawItem['id']
          delete newRawItem['hookFormId']

          dataItems.push(newDataItem)
          enrichedItems.push(newEnrichedItem)
          rawItems.push(newRawItem)
        }

        if (moduleName === 'Delivery') {
          if (recordData) {
            recordData.products = dataItems
          }
          if (recordEnriched) {
            recordEnriched.products = enrichedItems
          }
          recordRaw.products = rawItems
        } else {
          if (recordData) {
            recordData.items = dataItems
          }
          if (recordEnriched) {
            recordEnriched.items = enrichedItems
          }
          recordRaw.items = rawItems
        }
      }

      const body = {
        accountId,
        moduleName
      }
      if (recordData) {
        body.data = recordData
      }
      if (recordEnriched) {
        body.enriched = recordEnriched
      }
      if (recordRaw) {
        body.raw = recordRaw
      }

      await navigator.clipboard.writeText(JSON.stringify(body, null, 2))

      notification.info({
        message: `Record contents ${filter === 'top-section-only' ? '(top section only) ' : ''} have been copied to your clipboard`
      })
    },
    [accountId, form, moduleName]
  )

  const handlePaste = useCallback(
    /** @param {"all" | "empty-only"} emptyOnly */
    async (emptyOnly = 'all') => {
      const text = await navigator.clipboard.readText()
      if (!text) {
        notification.warning({
          message: 'No clipboard contents'
        })
      }

      let body = { accountId, moduleName, data: {}, enriched: {}, raw: {} }
      try {
        body = JSON.parse(text)
        if (!body.moduleName || (!body.data && !body.enriched)) {
          throw 'Clipboard contents do not appear to be copied from another record form'
        }
        if (body.moduleName !== moduleName) {
          throw `Incompatible module. The pasted data was for "${body.moduleName}" but this is a "${moduleName}" record.`
        }
        if (body.accountId !== accountId) {
          throw `Incompatible Account. The pasted data was from a different Account`
        }
      } catch (e) {
        notification.error({
          message: 'Problem parsing clipboard contents',
          description: String(e)
        })

        return
      }

      // Filter out Deleted or Removed product items.
      if (body.data && body.data.products && body.data.products.length > 0) {
        body.data.products = body.data.products.filter(
          item => !item.isDeleted && !item.isRemoved
        )
      }
      if (body.data && body.data.items && body.data.items.length > 0) {
        body.data.items = body.data.items.filter(
          item => !item.isDeleted && !item.isRemoved
        )
      }

      if (
        body.enriched &&
        body.enriched.products &&
        body.enriched.products.length > 0
      ) {
        body.enriched.products = body.enriched.products.filter(
          item => !item.isDeleted && !item.isRemoved
        )
      }
      if (
        body.enriched &&
        body.enriched.items &&
        body.enriched.items.length > 0
      ) {
        body.enriched.items = body.enriched.items.filter(
          item => !item.isDeleted && !item.isRemoved
        )
      }

      if (body.raw && body.raw.products && body.raw.products.length > 0) {
        body.raw.products = body.raw.products.filter(
          item => !item.isDeleted && !item.isRemoved
        )
      }
      if (body.raw && body.raw.items && body.raw.items.length > 0) {
        body.raw.items = body.raw.items.filter(
          item => !item.isDeleted && !item.isRemoved
        )
      }

      const formValues = form.getValues()
      const items = getLineItems()

      if (moduleName === 'Delivery') {
        if (formValues.data) {
          formValues.data.products = items
        }
        if (formValues.enriched) {
          formValues.enriched.products = items
        }
      } else {
        if (formValues.data) {
          formValues.data.items = items
        }
        if (formValues.enriched) {
          formValues.enriched.items = items
        }
      }

      const updatedData = _mergeValues(formValues.data, body.data, emptyOnly)
      const updatedRaw = _mergeValues(formValues.raw, body.raw, emptyOnly)
      const updatedEnriched = _mergeValues(
        formValues.enriched,
        body.enriched,
        emptyOnly
      )

      const updatedFormValues = {
        ...formValues,
        data: updatedData,
        enriched: updatedEnriched,
        raw: updatedRaw
      }

      // Reset the form with new "defaultValues" (will trigger changes to defaultValues in form).
      // Seems we need to do this twice to get it to stick.  Not sure why.
      form.reset(updatedFormValues, { keepDirty: true })
      form.reset(updatedFormValues, { keepDirty: true })

      // grab first Raw value and manually set it with the shouldDirty flag
      // don't worry about the value, we will be overwritting it in the next step
      if (updatedFormValues.raw) {
        const firstKey = Object.keys(updatedFormValues.raw)[0]
        form.setValue(`raw.${firstKey}`, '_', {
          shouldDirty: true
        })
        form.setValue(`raw.${firstKey}`, updatedFormValues.raw[firstKey], {
          shouldDirty: true
        })
      }

      // Force revalidation in case Input (Red) Validation rules are not met.
      // NOTE: This seems to fix some issues around the validation state for subsequent changes, but it doesn't
      // seem to actually trigger the validation immediately. There is a skipped test proving this.
      // The Warning validation does seem to occur, but it's unclear exactly how.
      form.trigger()

      notification.success({
        icon:
          emptyOnly === 'all' ? (
            <Icons.SnippetsFilled />
          ) : (
            <Icons.DiffOutlined />
          ),
        message: 'Changes applied from clipboard',
        description:
          emptyOnly === 'all'
            ? '💪 All fields overwritten'
            : '🤏 Empty fields have been filled'
      })
    },
    [accountId, form, moduleName]
  )

  if (props.type === 'copy') {
    useGlobalShortcut(Matchers.copyAndPaste.copyRecord, () => handleCopy())
  } else if (props.type === 'copyTopSectionOnly') {
    useGlobalShortcut(Matchers.copyAndPaste.copyRecordTopSectionOnly, () =>
      handleCopy('top-section-only')
    )
  } else if (props.type === 'paste') {
    useGlobalShortcut(Matchers.copyAndPaste.pasteEmpty, () =>
      handlePaste('empty-only')
    )
  } else {
    useGlobalShortcut(Matchers.copyAndPaste.pasteAll, () => handlePaste('all'))
  }

  if (!form) {
    return null
  }

  if (props.type === 'copy') {
    return (
      <div onClick={() => handleCopy()}>
        Copy Record {props.rawOrEnriched ? <span>(Raw fields only)</span> : ''}
      </div>
    )
  } else if (props.type === 'copyTopSectionOnly') {
    return (
      <div onClick={() => handleCopy('top-section-only')}>
        Copy Record (Top section, Raw fields only)
      </div>
    )
  } else if (props.type === 'paste') {
    return (
      <div onClick={() => handlePaste('empty-only')}>Paste (Empty Fields)</div>
    )
  } else {
    return <div onClick={() => handlePaste('all')}>Paste (All Fields)</div>
  }
}

/**
 *
 * @param {*} formValues
 * @param {*} pastedValues
 * @param {"all" | "empty-only"} emptyOnly
 * @returns {*}
 */
export function _mergeValues(
  formValues = {},
  pastedValues = {},
  emptyOnly = 'all'
) {
  const blocklist = _.keyBy([
    'deliveryId',
    'consignmentId',
    'deliveryArrivalDate',
    'deliveryArrivalTime',
    'deliveryExitDate',
    'deliveryExitTime',
    'deliveryDate',
    'removalDate',
    'removalTime',
    'removalExitDate',
    'removalExitTime'
  ])

  return _.mergeWith(
    _.cloneDeep(formValues),
    pastedValues,
    (oldValue, newValue, key) => {
      if (blocklist[key]) {
        return oldValue
      }

      const isEmpty =
        oldValue == null ||
        oldValue == '' ||
        oldValue == Guid.Empty ||
        (Array.isArray(oldValue) && oldValue.length === 0)

      if (emptyOnly === 'empty-only' && !isEmpty) {
        return oldValue
      }

      // if updating Products or Items use existing lineItem 'id'
      if ((key === 'products' || key === 'items') && !isEmpty) {
        return newValue.map((item, index) => {
          if (oldValue[index] && oldValue[index].id) {
            item.id = oldValue[index].id
          }
          return item
        })
      }

      return newValue
    }
  )
}
